import { useState } from 'react';
import { FileText, Table2, Columns3, Search, Check, CalendarDays, FolderOpen } from 'lucide-react';
import type { CoverPage } from '../privacy';

const tasks = [
  {
    name: '官网内容更新',
    team: '内容组',
    owner: '林悦',
    status: '进行中',
    hours: 16,
    done: 12,
    date: '周一',
  },
  {
    name: '客户需求整理',
    team: '产品组',
    owner: '陈晨',
    status: '已完成',
    hours: 8,
    done: 8,
    date: '周一',
  },
  {
    name: '季度数据核对',
    team: '运营组',
    owner: '李然',
    status: '进行中',
    hours: 24,
    done: 14,
    date: '周三',
  },
  {
    name: '设计规范整理',
    team: '设计组',
    owner: '许宁',
    status: '待开始',
    hours: 12,
    done: 0,
    date: '周四',
  },
  {
    name: '项目进度复盘',
    team: '产品组',
    owner: '陈晨',
    status: '待开始',
    hours: 6,
    done: 0,
    date: '周五',
  },
  {
    name: '知识库资料归档',
    team: '内容组',
    owner: '林悦',
    status: '已完成',
    hours: 10,
    done: 10,
    date: '周二',
  },
  {
    name: '周报模板优化',
    team: '运营组',
    owner: '李然',
    status: '进行中',
    hours: 8,
    done: 6,
    date: '周四',
  },
  {
    name: '新版本验收清单',
    team: '产品组',
    owner: '许宁',
    status: '待开始',
    hours: 16,
    done: 0,
    date: '周五',
  },
];
const stages = ['待开始', '进行中', '已完成'];
const stageClass = (stage: string) => ['queued', 'active', 'done'][stages.indexOf(stage)];

export function WorkCoverPage({
  page,
  notes,
  onNotesChange,
  noteSaved,
}: {
  page: Exclude<CoverPage, 'custom'>;
  notes: string;
  onNotesChange: (value: string) => void;
  noteSaved: boolean;
}) {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('全部状态');
  const rows = tasks.filter(
    (task) =>
      (!query || `${task.name} ${task.team} ${task.owner}`.includes(query.trim())) &&
      (filter === '全部状态' || task.status === filter),
  );
  const title =
    page === 'document' ? '项目计划与会议记录' : page === 'sheet' ? '项目进度汇总' : '本周工作安排';
  const Icon = page === 'document' ? FileText : page === 'sheet' ? Table2 : Columns3;
  return (
    <div className={`work-preset work-preset-view-${page}`}>
      <aside className="work-preset-sidebar">
        <span className="work-space-label">团队空间</span>
        <div>
          <FolderOpen size={17} />
          项目管理
        </div>
        <div className="work-space-current">
          <Icon size={17} />
          {page === 'document' ? '项目文档' : page === 'sheet' ? '数据表格' : '工作看板'}
        </div>
        <small>我的工作区</small>
        <span>团队资料</span>
        <span>每周例会</span>
        <span>历史归档</span>
        <p>让工作有序进行。</p>
      </aside>
      <main className="work-preset-content">
        <div className="work-preset-breadcrumb">
          项目管理 <span>/</span> 本周计划
        </div>
        <div className="work-preset-heading">
          <div>
            <span className="work-preset-symbol">
              <Icon size={25} />
            </span>
            <h1>{title}</h1>
          </div>
          <span className="work-date">
            <CalendarDays size={15} />
            {new Date().toLocaleDateString('zh-CN', { month: 'long', day: 'numeric' })}
          </span>
        </div>
        <p className="work-preset-subtitle">
          {page === 'document'
            ? '统一记录项目安排、讨论结论与后续行动。'
            : '同步团队目标，跟进每一个重要节点。'}
        </p>
        {page === 'document' ? (
          <section className="work-preset-document">
            <div className="work-preset-document-bar">
              <span>
                正文 <i /> 16 <i /> <b>B</b> <em>I</em>
              </span>
              <small>
                <Check size={13} />
                {noteSaved ? '已保存在本机' : '暂未保存，请保留页面'}
              </small>
            </div>
            <textarea
              className="work-preset-editor"
              aria-label="项目文档正文"
              maxLength={20000}
              spellCheck={false}
              value={notes}
              onChange={(e) => onNotesChange(e.target.value)}
            />
          </section>
        ) : (
          <>
            <div className="work-preset-metrics">
              <div>
                <span>任务总数</span>
                <b>
                  {tasks.length}
                  <small>项</small>
                </b>
              </div>
              <div>
                <span>正在推进</span>
                <b>
                  3<small>项</small>
                </b>
              </div>
              <div>
                <span>已完成</span>
                <b>
                  2<small>项</small>
                </b>
              </div>
              <div>
                <span>计划工时</span>
                <b>
                  100<small>小时</small>
                </b>
              </div>
            </div>
            <div className="work-preset-filters">
              <label className="work-preset-search">
                <Search size={16} />
                <input
                  aria-label="搜索项目或负责人"
                  placeholder="搜索项目或负责人"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </label>
              <select
                aria-label="任务状态"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              >
                <option>全部状态</option>
                {stages.map((stage) => (
                  <option key={stage}>{stage}</option>
                ))}
              </select>
            </div>
            {page === 'sheet' ? (
              <section className="work-preset-table-wrap" aria-label="项目数据表格" tabIndex={0}>
                <table className="work-preset-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>项目名称</th>
                      <th>负责团队</th>
                      <th>负责人</th>
                      <th>状态</th>
                      <th>计划工时</th>
                      <th>进度</th>
                      <th>完成节点</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((task, index) => (
                      <tr key={task.name}>
                        <td>{index + 1}</td>
                        <td>{task.name}</td>
                        <td>{task.team}</td>
                        <td>
                          <span className="work-owner">{task.owner.slice(0, 1)}</span>
                          {task.owner}
                        </td>
                        <td>
                          <span className={`work-status ${stageClass(task.status)}`}>
                            {task.status}
                          </span>
                        </td>
                        <td>{task.hours} h</td>
                        <td>
                          <span className="work-progress">
                            <i style={{ width: `${(task.done / task.hours) * 100}%` }} />
                          </span>
                          <small>{Math.round((task.done / task.hours) * 100)}%</small>
                        </td>
                        <td>{task.date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="work-preset-table-footer">
                  {rows.length ? `共 ${rows.length} 项记录` : '没有找到匹配的记录'}
                </div>
              </section>
            ) : (
              <div className="work-preset-board">
                {stages.map((stage) => {
                  const cards = rows.filter((task) => task.status === stage);
                  return (
                    <section className={`work-board-column ${stageClass(stage)}`} key={stage}>
                      <h2>
                        <i />
                        {stage}
                        <span>{cards.length}</span>
                      </h2>
                      {cards.map((task) => (
                        <article className="work-task-card" key={task.name}>
                          <span className="work-task-team">{task.team}</span>
                          <h3>{task.name}</h3>
                          <p>确认任务细节，同步阶段进展与交付安排。</p>
                          <div>
                            <span>
                              <span className="work-owner">{task.owner.slice(0, 1)}</span>
                              {task.owner}
                            </span>
                            <small>{task.date}</small>
                          </div>
                        </article>
                      ))}
                      {!cards.length && <p className="work-board-empty">暂无匹配任务</p>}
                    </section>
                  );
                })}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
